/**
 * PitfallCard — queue #666 §UAT-2026-07-09-R666 §R666-4.
 *
 * 함정 사전 카드 그리드의 카드 1건 — 제목 + 심각도 뱃지 + 관련 모듈 코드 배지 목록 +
 * 증상 1줄. 클릭 시 호출부(PitfallDictionary → CommonShowcasePage)가 `?pitfall=<id>` 로
 * 상세 전환한다(§R664 RecipeGallery 카드 계보).
 */
import { useTranslation } from 'react-i18next';
import { StatusPill, type StatusTone } from '@ui-kit';
import type { Pitfall, PitfallSeverity } from '../data/pitfalls';

/** 심각도 → StatusPill tone 매핑(PitfallDetailPanel/TroubleshootingSection 이 재사용). */
export function severityTone(severity: PitfallSeverity): StatusTone {
  if (severity === 'high') return 'danger';
  if (severity === 'medium') return 'warning';
  return 'info';
}

interface Props {
  pitfall: Pitfall;
  onSelect: (id: string) => void;
}

export function PitfallCard({ pitfall, onSelect }: Props) {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      data-testid={`pitfall-card-${pitfall.id}`}
      onClick={() => onSelect(pitfall.id)}
      style={{
        textAlign: 'left',
        padding: 'var(--spacing-4)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--color-surface-background)',
        cursor: 'pointer',
        fontFamily: 'var(--font-family-sans)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-2)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--spacing-2)' }}>
        <h3
          style={{
            margin: 0,
            fontSize: 'var(--font-size-base)',
            fontWeight: 700,
            color: 'var(--color-text-primary)',
          }}
        >
          {t(pitfall.titleKey)}
        </h3>
        <StatusPill tone={severityTone(pitfall.severity)}>
          {t(`showcase.pitfall.severity.${pitfall.severity}`)}
        </StatusPill>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-1)' }}>
        {pitfall.moduleCodes.map((code) => (
          <code
            key={code}
            style={{
              fontFamily: 'var(--font-family-mono)',
              fontSize: 'var(--font-size-xs)',
              background: 'var(--color-surface-elevated)',
              border: '1px solid var(--color-surface-border)',
              padding: '2px var(--spacing-2)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--color-brand-primary)',
            }}
          >
            {code}
          </code>
        ))}
      </div>

      <p
        style={{
          margin: 0,
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-secondary)',
        }}
      >
        {t(pitfall.symptomKey)}
      </p>
    </button>
  );
}
